import { createSegmentationState, type SegmentationState } from "./useSegmentation";

export interface SuggestedSegment {
	readonly segment_id: string;
	readonly start_sec: number;
	readonly end_sec: number;
	readonly label: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function finiteNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function uniqueSortedTimes(times: readonly number[]): number[] {
	return [...new Set(times)].sort((a, b) => a - b);
}

export function parseSuggestedSegments(value: unknown): SuggestedSegment[] | undefined {
	if (!Array.isArray(value)) return undefined;

	const segments = value.flatMap((item): SuggestedSegment[] => {
		if (!isRecord(item)) return [];

		const startSec = finiteNumber(item.start_sec);
		const endSec = finiteNumber(item.end_sec);
		const segmentId = typeof item.segment_id === "string" ? item.segment_id : "";
		const label = typeof item.label === "string" ? item.label : "";
		if (
			!segmentId ||
			!label ||
			startSec === null ||
			endSec === null ||
			startSec < 0 ||
			endSec < 0 ||
			endSec <= startSec
		) {
			return [];
		}

		return [
			{
				segment_id: segmentId,
				start_sec: startSec,
				end_sec: endSec,
				label,
			},
		];
	});

	return segments.length > 0 ? segments : undefined;
}

export function createSegmentationStateFromSuggestedSegments(
	videoId: string,
	duration: number,
	suggestedSegments: readonly SuggestedSegment[],
): SegmentationState {
	if (!Number.isFinite(duration) || duration <= 0 || suggestedSegments.length === 0) {
		return createSegmentationState(videoId, duration);
	}

	const sortedSegments = suggestedSegments
		.map((segment) => ({
			...segment,
			start_sec: Math.max(0, Math.min(segment.start_sec, duration)),
			end_sec: Math.max(0, Math.min(segment.end_sec, duration)),
		}))
		.filter((segment) => segment.end_sec > segment.start_sec)
		.sort((a, b) => a.start_sec - b.start_sec || a.end_sec - b.end_sec);

	if (sortedSegments.length === 0) return createSegmentationState(videoId, duration);

	const hasOverlap = sortedSegments.some(
		(segment, index) => index > 0 && segment.start_sec < sortedSegments[index - 1].end_sec,
	);
	if (hasOverlap) return createSegmentationState(videoId, duration);

	// N 個の提案セグメントから N-1 個の内部境界を作る。各セグメントの end_sec を
	// 仕切りとして使い、最後のセグメントの end_sec は除外する（末端は duration が
	// 暗黙の終端になるため）。これにより最終セグメントが duration 未満で終わって
	// いても余分な無ラベルセグメントが生じない。
	const boundaries = uniqueSortedTimes(
		sortedSegments
			.slice(0, -1)
			.map((segment) => segment.end_sec)
			.filter((time) => time > 0 && time < duration),
	);

	const labels = sortedSegments.map((segment) => segment.label);

	return {
		videoId,
		duration,
		boundaries,
		labels,
	};
}
